import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cfpBaseUrl, wfsBaseUrl } from '../../network/urls';

const CACHE_KEY_PREFIX = 'geoJson_cache_';
const CACHE_EXPIRY_HOURS = 24; // Cache expires after 24 hours

// Helper function to get cached data
const getCachedGeoJson = (officeCode) => {
    try {
        const cacheKey = CACHE_KEY_PREFIX + officeCode;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            const { data, timestamp } = JSON.parse(cached);
            const now = new Date().getTime();
            const expiryTime = CACHE_EXPIRY_HOURS * 60 * 60 * 1000;

            // Check if cache is still valid
            if (now - timestamp < expiryTime) {
                console.log('GeoJSON loaded from cache for:', officeCode);
                return data;
            } else {
                // Cache expired, remove it
                localStorage.removeItem(cacheKey);
            }
        }
    } catch (error) {
        console.error('Error reading GeoJSON cache:', error);
    }
    return null;
};

// Helper function to set cached data
const setCachedGeoJson = (officeCode, data) => {
    try {
        const cacheKey = CACHE_KEY_PREFIX + officeCode;
        const cacheData = {
            data: data,
            timestamp: new Date().getTime()
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        console.log('GeoJSON cached for:', officeCode);
    } catch (error) {
        console.error('Error caching GeoJSON:', error);
        // If localStorage is full, clear old geoJson caches
        clearOldGeoJsonCaches();
    }
};

// Helper function to clear old caches if storage is full
const clearOldGeoJsonCaches = () => {
    try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(CACHE_KEY_PREFIX)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log('Cleared old GeoJSON caches');
    } catch (error) {
        console.error('Error clearing GeoJSON caches:', error);
    }
};

const initialState = {
    data: null,
    isLoading: false,
    error: null,
}

export const fetchGeoJson = createAsyncThunk(
    'complaints/fetchGeoJsonArray',
    async ({ subDivisionCode, roleTypeId }, { getState }) => {
        const { auth } = getState();
        const { wmsPrivateToken, code, hierarchy, token } = auth;

        let codeToApi = subDivisionCode

        if (roleTypeId == 1) {
            codeToApi = codeToApi.toString().slice(0, 2)
        }

        const finalCode = codeToApi == 10 ? 15 : codeToApi;

        // Check cache first
        const cachedData = getCachedGeoJson(finalCode);
        if (cachedData) {
            return { data: cachedData, fromCache: true };
        }

        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
            "officeCode": finalCode
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        const response = await fetch(cfpBaseUrl + "lineComplaints/getGISMapping", requestOptions);
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Geo Json API issue!');
        }

        const result = await response.json();

        if (result.Message === "Data Not Found.") {
            throw new Error(result.Message, 'printing error message' || 'Unable to fetch stats!');
        }

        // Cache the result
        if (result.data) {
            setCachedGeoJson(finalCode, result.data);
        }

        return result;
    }
);

const geoJsonSlice = createSlice({
    name: 'geoJsonArray',
    initialState: initialState,
    reducers: {
        resetGeoJsonState(state) {
            state.data = null;
            state.isLoading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGeoJson.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchGeoJson.fulfilled, (state, action) => {
                state.isLoading = false;
                if (action.payload) {
                    // Handle both cached and fresh data
                    state.data = action.payload.fromCache ? action.payload.data : action.payload.data;
                } else {
                    state.data = null
                }
                state.error = null;
            })
            .addCase(fetchGeoJson.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message;
            });
    },
});

export const { resetGeoJsonState } = geoJsonSlice.actions;

export default geoJsonSlice.reducer;
